import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DateTimeService } from '@y42/date-time';
import { GitBlameLine, GitService } from '@y42/git';

@Component({
  selector: 'y42-git-file-blame',
  templateUrl: './git-file-blame.component.html',
  styleUrls: ['./git-file-blame.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GitFileBlameComponent {
  constructor(private readonly gitService: GitService, private readonly dateTimeService: DateTimeService) {}

  @Input() path!: string;

  @Input() commitSha!: string;

  async annotate(editor: monaco.editor.IStandaloneCodeEditor) {
    let maxCountCharsInLine = 0;
    let text = '';
    editor.updateOptions({
      fixedOverflowWidgets: true,
    });

    let blameLines: GitBlameLine[] = [];
    try {
      blameLines = await this.gitService.getBlameLines({
        path: this.path,
        commitSha: this.commitSha,
      });
    } catch (err) {
      // file was removed
      if (err instanceof Error && err.message.includes('does not exist in the given tree')) {
        console.error(err.message);
      }
    }
    for (const blameLine of blameLines) {
      maxCountCharsInLine = Math.max(maxCountCharsInLine, (blameLine.sha + blameLine.author).length);
      text += `${blameLine.text}\n`;
    }

    editor.setValue(text);

    maxCountCharsInLine += 10;
    const blameText = (sha: string, name: string) =>
      sha + '.'.repeat(maxCountCharsInLine - sha.length - name.length) + name;

    const decorations: monaco.editor.IModelDeltaDecoration[] = blameLines.map((blameLine) => {
      const { sha, author, lineNumber, title, description, date } = blameLine;

      const content = blameText(sha, author);

      return {
        range: new monaco.Range(lineNumber, 0, lineNumber, 0),
        options: {
          isWholeLine: false,
          before: {
            content,
            inlineClassName: 'blame-content',
          },
          hoverMessage: [
            {
              value: [
                `_${this.dateTimeService.formatDateRelativeToNow(date || new Date())}_`,
                `**${author}**`,
                title,
                description,
              ].join('\n\n'),
            },
          ],
        },
      };
    });
    editor.deltaDecorations([], decorations);

    const domNode = editor.getDomNode();
    if (domNode) {
      domNode.style.height = `${editor.getContentHeight()}px`;
    }
  }
}
